import { canDuplicateAtCursor } from "@/components/input/utils/duplicateNodeAtCursor";
import { isSelectionLocked } from "@/components/tiptap/lock/utils";
import { displayHotkey } from "@/utils/hotkeys";

import type { IconName } from "@/components/icons/Icon";
import type {
  ClipboardCommand,
  ClipboardCommandSuggestion,
  SuggestionProvider,
} from "@/components/suggestions/types";
import type { DocumentModel } from "@/models/DocumentModel";
import type { Hotkey } from "@/utils/hotkeys";

const COMMANDS: ClipboardCommand[] = ["copy", "cut", "paste", "duplicate"];

const COMMAND_ICON: Record<ClipboardCommand, IconName> = {
  copy: "Copy",
  cut: "Cut",
  paste: "Paste",
  duplicate: "Duplicate",
  "duplicate-at-cursor": "Duplicate",
  "reference-at-cursor": "Link",
};

const COMMAND_HOTKEY: Record<ClipboardCommand, Hotkey> = {
  copy: "copy",
  cut: "cut",
  paste: "paste",
  duplicate: "duplicate-line",
  "duplicate-at-cursor": "duplicate-at-cursor",
  "reference-at-cursor": "reference-at-cursor",
};

/**
 * `hasTarget` counts a right-clicked node as something to act on: it is selected
 * only once the command runs, so the selection alone would disable copy and cut.
 */
function isDisabled(
  doc: DocumentModel,
  command: ClipboardCommand,
  locked: boolean,
  hasTarget: boolean,
): boolean {
  switch (command) {
    case "paste": {
      return locked || !doc.editor.isClipboardReadable;
    }

    case "cut": {
      return locked || !hasTarget;
    }

    case "copy": {
      return !hasTarget;
    }

    case "duplicate":
    case "duplicate-at-cursor":
    case "reference-at-cursor": {
      return locked;
    }

    default: {
      return false;
    }
  }
}

export const clipboardCommandSuggestionsProvider: SuggestionProvider<"clipboardCommand"> = {
  type: "clipboardCommand",
  allowedWhenLocked: true,
  search: (_str, doc) => {
    const { hasActionableSelection } = doc.selection;
    const { pointerAnchor, pointerNode } = doc.editor.suggestions;

    if (!hasActionableSelection && !pointerAnchor) {
      return [];
    }

    let commands: ClipboardCommand[] = [...COMMANDS];

    const isComputed = !!doc.computedNodes.selectedComputedNode;
    if (isComputed) {
      commands = commands.filter((command) => command !== "duplicate");
    }

    // With the caret already against the node, the copy would land where it sits.
    if (pointerNode && canDuplicateAtCursor(doc, pointerNode)) {
      commands.push(isComputed ? "reference-at-cursor" : "duplicate-at-cursor");
    }

    const locked = isSelectionLocked(doc);
    const hasTarget = hasActionableSelection || !!pointerNode;

    const suggestions: ClipboardCommandSuggestion[] = commands.map((command) => ({
      type: "clipboardCommand",
      command,
      trackingId: command,
      popupGroup: "clipboard",
      priority: 1,
      icon: COMMAND_ICON[command],
      hotkey: displayHotkey(COMMAND_HOTKEY[command]),
      disabled: isDisabled(doc, command, locked, hasTarget),
      canBeAutocompleted: false,
    }));

    return [suggestions];
  },
};
